import {
  fetchOffersCard,
  fetchOfferTableAndChart,
  fetchOfferByDepartmentVisual,
  fetchOfferByLocationVisual,
  fetchOfferByRejectionReason,
  fetchOfferDrilldown,
} from './actions';
import { getDefaultAtsFilters, buildReportsSlice } from '../common/ats-filters';
import { flatTransformer, getTableData } from '../common/flat-transformer';
import { cardTransformer } from '../common/card-transformer';
import { visualPayload } from '../common/visual-transformer';
import { getPercentage } from '../common/get-percentage';

const getChartData = (response) => {
  const {
    chart = [],
    headers,
    lookups: { dt, g1, g2 } = {},
  } = response?.data || {};
  return flatTransformer({ headers, datas: chart, g1, g2, dt });
};

const constructDrilldownData = (payload) => {
  const k1Map = payload?.data?.lookups?.k1;
  const k2Map = payload?.data?.lookups?.k2;
  const k3Map = payload?.data?.lookups?.k3;
  const k4Map = payload?.data?.lookups?.k4;
  const k5Map = payload?.data?.lookups?.k5;
  const k6Map = payload?.data?.lookups?.k6;
  const k7Map = payload?.data?.lookups?.k7;
  const returnData = payload?.data?.table?.map((data) => {
    // eslint-disable-next-line no-unused-vars
    const [
      ,
      k1,
      k2,
      k3,
      k4,
      k5,
      k6,
      k7,
      time_to_offer,
      time_to_close_offer,
      revisions_on_data,
    ] = data;
    return {
      ...k1Map?.[k1],
      offer_status: k2Map?.[k2],
      ...k3Map?.[k3],
      ...k4Map?.[k4],
      ...k5Map?.[k5],
      offer_created_by: k6Map?.[k6],
      requisition_code: k7Map?.[k7],
      time_to_offer,
      time_to_close_offer,
      revisions_on_data,
    };
  });

  return returnData;
};

const tableWithTotal = (data) => {
  const offers_total = data?.reduce((acc, curr) => acc + curr?.offers, 0);

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      return {
        ...datum,

        offers_total,
        offers_per: getPercentage(datum?.offers, offers_total),
        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const initialState = {
  ...getDefaultAtsFilters(),

  isCardLoading: false,
  cards: {},

  chart: [],
  table: [],
  isChartLoading: false,
  isTableLoading: false,

  hiresByDepartment: [],
  isHiresByDepartmentLoading: false,

  hiresBySource: [],
  isHiresBySourceLoading: false,

  offerRejectionByReason: [],
  isOfferRejectionByReasonLoading: [],
  isDrillLoading: false,
  initialLoading: false,
  drilldownData: [],
  stageMapping: {},
  totalCount: 0,
  currentDrillPage: 1,
};

const extraReducers = {
  [fetchOffersCard.pending]: (state) => {
    state.isCardLoading = true;
  },
  [fetchOffersCard.fulfilled]: (state, { payload }) => {
    state.cards = cardTransformer(payload);
    state.isCardLoading = false;
  },
  [fetchOffersCard.rejected]: (state) => {
    state.isCardLoading = false;
  },

  [fetchOfferTableAndChart.pending]: (state) => {
    state.isChartLoading = true;
    state.isTableLoading = true;
  },

  [fetchOfferTableAndChart.fulfilled]: (state, { meta, payload = {} }) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
    state.chart = getChartData(payload);
    state.table = tableWithTotal(getTableData(payload, meta?.arg));
  },

  [fetchOfferTableAndChart.rejected]: (state) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
  },

  [fetchOfferByDepartmentVisual.pending]: (state) => {
    state.isHiresByDepartmentLoading = true;
  },

  [fetchOfferByDepartmentVisual.fulfilled]: (state, { payload = {} }) => {
    state.hiresByDepartment = visualPayload(payload);
    state.isHiresByDepartmentLoading = false;
  },

  [fetchOfferByDepartmentVisual.rejected]: (state) => {
    state.isHiresByDepartmentLoading = false;
  },

  [fetchOfferByLocationVisual.pending]: (state) => {
    state.isHiresBySourceLoading = true;
  },
  [fetchOfferByLocationVisual.fulfilled]: (state, { payload }) => {
    state.hiresBySource = visualPayload(payload);
    state.isHiresBySourceLoading = false;
  },
  [fetchOfferByLocationVisual.rejected]: (state) => {
    state.isHiresBySourceLoading = false;
  },

  [fetchOfferByRejectionReason.pending]: (state) => {
    state.isOfferRejectionByReasonLoading = true;
  },
  [fetchOfferByRejectionReason.fulfilled]: (state, { payload }) => {
    state.offerRejectionByReason = visualPayload(payload);
    state.isOfferRejectionByReasonLoading = false;
  },
  [fetchOfferByRejectionReason.rejected]: (state) => {
    state.isOfferRejectionByReasonLoading = false;
  },
  [fetchOfferDrilldown.pending]: (state, { meta }) => {
    const { drilldown } = meta?.arg || {};
    if (drilldown.page === 1) {
      state.initialLoading = true;
    } else {
      state.isDrillLoading = true;
    }
  },
  [fetchOfferDrilldown.fulfilled]: (state, { meta, payload = {} }) => {
    if (!payload) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
    const { drilldown } = meta?.arg || {};
    const newData = constructDrilldownData(payload);
    state.totalCount = payload?.data?.pagination?.total_count;
    if (drilldown.page === 1) {
      state.drilldownData = newData;
    } else {
      state.drilldownData = [...state.drilldownData, ...newData];
    }
    state.currentDrillPage = drilldown.page;
  },
  [fetchOfferDrilldown.rejected]: (state, action) => {
    if (
      action.error?.name === 'AbortError' ||
      action.error?.name === 'CanceledError'
    ) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
  },
};

export const { reducer, actions } = buildReportsSlice({
  name: 'atsOffersReport',
  initialState,
  extraReducers,
});

export const {
  applyFilter,
  resetFilter,
  applyTimeFrame,
  applyViewBy,
  applyGroup,
  setFiltersData,
  applySearch,
  resetDrillDown,
} = actions;
export default reducer;
